/**
 * Empty-state dashboard for a fresh workspace. Mirrors
 * `design/Syntra/dashboard-empty.html`:
 *
 *   - A KPI shell with neutral zeros (so the layout reads as "not yet filled").
 *   - An onboarding hero with a soft red radial glow + two CTAs.
 *   - A "How Syntra works" 4-step strip.
 */
import {
  BarChart3,
  Building2,
  Calculator,
  DollarSign,
  GitFork,
  Plus,
  Search,
  TrendingUp,
  Wallet,
  Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";

const KPIS: Array<{
  label: string;
  icon: typeof DollarSign;
  accent: string;
  value: string;
  subIcon: typeof DollarSign;
  sub: string;
}> = [
  {
    label: "Portfolio Value",
    icon: DollarSign,
    accent: "bg-blue-500/15 text-blue-400",
    value: "$0",
    subIcon: Building2,
    sub: "No properties yet",
  },
  {
    label: "Total Equity",
    icon: TrendingUp,
    accent: "bg-success/15 text-success",
    value: "$0",
    subIcon: TrendingUp,
    sub: "Acquire a property to build equity",
  },
  {
    label: "Monthly Cash Flow",
    icon: Wallet,
    accent: "bg-warning/15 text-warning",
    value: "$0",
    subIcon: Wallet,
    sub: "No active rentals yet",
  },
  {
    label: "Pipeline Deals",
    icon: GitFork,
    accent: "bg-primary/15 text-primary",
    value: "0",
    subIcon: GitFork,
    sub: "No deals in the pipeline",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Find",
    description: "Search the market and shortlist deals worth a closer look.",
    icon: Search,
  },
  {
    num: "02",
    title: "Analyze",
    description: "Underwrite cash flow, cap rate, and returns before you commit.",
    icon: Calculator,
  },
  {
    num: "03",
    title: "Acquire",
    description: "Track each deal through the pipeline and rehab to close.",
    icon: Workflow,
  },
  {
    num: "04",
    title: "Track",
    description: "Monitor portfolio value, equity, and cash flow right here.",
    icon: BarChart3,
  },
] as const;

export function DashboardEmptyState() {
  return (
    <div className="flex flex-col gap-3.5">
      {/* KPI shell with neutral zeros */}
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        {KPIS.map((tile) => (
          <div
            key={tile.label}
            className="flex flex-col gap-2.5 rounded-[10px] border border-border bg-card p-[18px]"
          >
            <div className="flex items-start justify-between gap-2.5">
              <span className="text-[13px] font-semibold text-muted-foreground">
                {tile.label}
              </span>
              <span
                className={`grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-[9px] ${tile.accent}`}
              >
                <tile.icon className="h-4 w-4" aria-hidden />
              </span>
            </div>
            <div className="text-[26px] font-extrabold leading-[1.15] tabular-nums text-muted-foreground/60">
              {tile.value}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <tile.subIcon className="h-[13px] w-[13px] flex-shrink-0" aria-hidden />
              {tile.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Onboarding hero */}
      <div
        className="flex flex-col items-center rounded-[14px] border border-border bg-card px-7 pb-11 pt-12 text-center"
        style={{
          backgroundImage:
            "radial-gradient(120% 90% at 50% -10%, hsl(var(--primary) / 0.10), transparent 60%)",
        }}
      >
        <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-primary/12 text-primary shadow-[0_6px_22px_hsl(var(--primary)/0.18)]">
          <Building2 className="h-[30px] w-[30px]" aria-hidden />
        </div>
        <h2 className="mb-2.5 text-[22px] font-extrabold text-foreground">
          Let's build your portfolio
        </h2>
        <p className="mb-6 max-w-[520px] text-sm leading-relaxed text-muted-foreground">
          You haven't added any properties yet. Find a deal, underwrite the
          numbers, and move it through your pipeline — this dashboard fills in
          automatically with live performance data as you go.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            asChild
            className="shadow-[0_4px_14px_hsl(var(--primary)/0.28)]"
          >
            <Link to="/deals">
              <Search className="h-4 w-4" />
              Find your first deal
            </Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/properties">
              <Plus className="h-4 w-4" />
              Add a property
            </Link>
          </Button>
        </div>
      </div>

      {/* How it works */}
      <div>
        <h2 className="mb-3 mt-3 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
          How Syntra works
        </h2>
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          {STEPS.map((step) => (
            <div
              key={step.num}
              className="relative rounded-xl border border-border bg-card px-5 py-[18px]"
            >
              <span className="absolute right-[18px] top-4 text-xs font-extrabold text-border">
                {step.num}
              </span>
              <div className="mb-3 grid h-[38px] w-[38px] place-items-center rounded-[10px] border border-border bg-muted text-muted-foreground">
                <step.icon className="h-[18px] w-[18px]" aria-hidden />
              </div>
              <p className="mb-[5px] text-sm font-bold text-foreground">
                {step.title}
              </p>
              <p className="text-[12.5px] leading-[1.5] text-muted-foreground">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
